// Real-time Flow Graph Validation Badge (PRD §1.9, WP-08).
// Surfaces graph structural integrity issues, dangling handles, and compliance warnings.
import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { CheckCircleIcon, AlertTriangleIcon, ChevronDownIcon, RefreshIcon } from '../icons';

interface FlowValidationBadgeProps {
  valid: boolean;
  errors: string[];
  warnings: string[];
  isValidating?: boolean;
  onValidate?: () => void;
}

export default function FlowValidationBadge({
  valid,
  errors,
  warnings,
  isValidating = false,
  onValidate,
}: FlowValidationBadgeProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  const totalIssues = errors.length + warnings.length;

  if (valid && totalIssues === 0) {
    return (
      <div className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
        <CheckCircleIcon className="h-3.5 w-3.5" />
        <span>{t('flow.validation.valid') || 'Graph Valid'}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800 dark:bg-amber-950/50 dark:text-amber-300 border border-amber-300 dark:border-amber-800"
      >
        <AlertTriangleIcon className="h-3.5 w-3.5 text-danger" />
        <span>
          {errors.length > 0
            ? `${errors.length} Error(s)`
            : `${warnings.length} Warning(s)`}
        </span>
        <ChevronDownIcon className="h-3 w-3" />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-20 cursor-default"
          />
          <div className="absolute end-0 top-full z-30 mt-1.5 w-72 sm:w-80 rounded-2xl border border-line bg-white p-3 shadow-xl dark:border-slate-800 dark:bg-slate-900 text-xs">
            <div className="flex items-center justify-between pb-2 border-b border-line dark:border-slate-800 mb-2">
              <span className="font-bold text-ink dark:text-white">Validation Results</span>
              {onValidate && (
                <button
                  type="button"
                  onClick={onValidate}
                  disabled={isValidating}
                  className="flex items-center gap-1 text-[11px] text-brand hover:underline"
                >
                  <RefreshIcon className="h-3 w-3" /> Revalidate
                </button>
              )}
            </div>

            <div className="space-y-2 max-h-56 overflow-y-auto">
              {errors.map((err, i) => (
                <div
                  key={`err_${i}`}
                  className="rounded-lg bg-pink-50 p-2 text-danger dark:bg-pink-950/40 dark:text-pink-300 text-[11px] leading-relaxed"
                >
                  ✕ {err}
                </div>
              ))}
              {warnings.map((warn, i) => (
                <div
                  key={`warn_${i}`}
                  className="rounded-lg bg-amber-50 p-2 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 text-[11px] leading-relaxed"
                >
                  ⚠ {warn}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
